export interface ReferentielCompetences {
  competences: Record<
    string,
    {
      ordre: number;
      color: string;
      aptitudes: Record<string, { cle_competence: string; ordre: number }>;
    }
  >;
}

export interface AutodiagReponse {
  name: string;
  titre?: string;
  reponse: string;
  sous_questions?: { reponse: string }[];
}

export interface FamilleResult {
  nom: string;
  color: string;
  ordre: number;
  aptitudes: { nom: string; categorie: string; ordre: number }[];
}

export const categorieColors: Record<string, string> = {
  A: "#318300",
  B: "#70C03F",
  C: "#F09C4F",
  D: "#D8365F",
};

export const categorieLabels: Record<string, string> = {
  A: "À valoriser",
  B: "À capitaliser",
  C: "À conforter",
  D: "À développer",
};

export function scoreToCategorie(score: number): string {
  if (score >= 3.5) return "A";
  if (score >= 2.5) return "B";
  if (score >= 1.5) return "C";
  return "D";
}

export function buildFamillesAutodiag(
  reponses: Record<string, AutodiagReponse>,
  referentiel: ReferentielCompetences,
): FamilleResult[] {
  const cleToInfo: Record<
    string,
    {
      familleName: string;
      aptitudeNom: string;
      familleColor: string;
      familleOrdre: number;
      aptitudeOrdre: number;
    }
  > = {};

  for (const [familleName, familleData] of Object.entries(
    referentiel.competences,
  )) {
    for (const [aptitudeNom, aptitudeData] of Object.entries(
      familleData.aptitudes,
    )) {
      cleToInfo[aptitudeData.cle_competence] = {
        familleName,
        aptitudeNom,
        familleColor: familleData.color,
        familleOrdre: familleData.ordre,
        aptitudeOrdre: aptitudeData.ordre,
      };
    }
  }

  const map = new Map<string, FamilleResult>();

  for (const q of Object.values(reponses)) {
    const info = cleToInfo[q.name];
    if (!info) continue;

    const globalScore = parseFloat(q.reponse);
    const sousScores = (q.sous_questions ?? []).map((sq) =>
      parseFloat(sq.reponse),
    );
    const avgScore =
      sousScores.length > 0
        ? (globalScore +
            sousScores.reduce((a, b) => a + b, 0) / sousScores.length) /
          2
        : globalScore;

    if (!map.has(info.familleName)) {
      map.set(info.familleName, {
        nom: info.familleName,
        color: info.familleColor,
        ordre: info.familleOrdre,
        aptitudes: [],
      });
    }
    map.get(info.familleName)!.aptitudes.push({
      nom: q.titre ?? info.aptitudeNom,
      categorie: scoreToCategorie(avgScore),
      ordre: info.aptitudeOrdre,
    });
  }

  return [...map.values()]
    .sort((a, b) => a.ordre - b.ordre)
    .map((f) => ({
      ...f,
      aptitudes: f.aptitudes.sort((a, b) => a.ordre - b.ordre),
    }));
}

export function buildFamillesInbasket(
  clientResults: Record<string, string>,
  referentiel: ReferentielCompetences,
): FamilleResult[] {
  const aptitudeToFamily: Record<
    string,
    {
      familleNom: string;
      familleColor: string;
      familleOrdre: number;
      aptitudeOrdre: number;
    }
  > = {};

  for (const [familleNom, familleData] of Object.entries(
    referentiel.competences,
  )) {
    for (const [aptitudeNom, aptitudeData] of Object.entries(
      familleData.aptitudes,
    )) {
      aptitudeToFamily[aptitudeNom] = {
        familleNom,
        familleColor: familleData.color,
        familleOrdre: familleData.ordre,
        aptitudeOrdre: aptitudeData.ordre,
      };
    }
  }

  const map = new Map<string, FamilleResult>();

  for (const [aptitudeNom, categorie] of Object.entries(clientResults)) {
    const info = aptitudeToFamily[aptitudeNom];
    if (!info) continue;

    if (!map.has(info.familleNom)) {
      map.set(info.familleNom, {
        nom: info.familleNom,
        color: info.familleColor,
        ordre: info.familleOrdre,
        aptitudes: [],
      });
    }
    map.get(info.familleNom)!.aptitudes.push({
      nom: aptitudeNom,
      categorie,
      ordre: info.aptitudeOrdre,
    });
  }

  return [...map.values()]
    .sort((a, b) => a.ordre - b.ordre)
    .map((f) => ({
      ...f,
      aptitudes: f.aptitudes.sort((a, b) => a.ordre - b.ordre),
    }));
}
